// Vendor imports
const classNames = require('classnames')
const _ = require('lodash')
const React = require('react')
// Shared imports
const BemEntity = require('shared/bem')
const {
  TYPES: modelTypes,
  ARTICLE_SUBTYPES: articleSubtypes,
  ARTICLE_SUBTYPES_KEYS: articleSubTypesKeys
} = require('shared/model-types')
// Client V2 common imports
const { CapsuleButton } = require('client-v2/common/components/buttons')
const EmbeddedPlayer = require('client-v2/common/components/embedded-player')
const LazyImage = require('client-v2/common/components/lazy-image')
const UserAvatar = require('client-v2/common/components/user-avatar')
const {
  usePropsCallback,
  usePropsEventCallback
} = require('client-v2/common/hooks/callbacks')
const useLineClampFitStyle = require('client-v2/common/hooks/line-clamp')
const useSymbol = require('client-v2/common/hooks/symbol')
const {
  use10PctVisibleViewportObserver,
  use50PctVisibleViewportObserver,
  use90PctVisibleViewportObserver,
  use100PctVisibleViewportObserver
} = require('client-v2/common/hooks/viewport-observer')
const {
  articleDomainSearchHref
} = require('client-v2/common/models/article/actions')
const {
  userProfileHref
} = require('client-v2/common/models/user/actions')
const tag = require('client-v2/common/utils/hyperscript')
// Sibling imports
const SeriesFeedArticleActions = require('./article-actions')

const bem = BemEntity('SeriesFeedArticle')

function SeriesFeedArticle(props) {
  const { article, height, recReason, onArticleElementFocus } = props
  // Prefer the subtype enum key in the stylesheet instead of the value
  const subtypeKey = articleSubTypesKeys[article.subtype]
  const anchorId = useSymbol() // Used for LWE Modal positioning
  const handleContentNavigate = usePropsEventCallback(props, 'onArticleContentNavigate')
  const handleDomainNavigate = usePropsEventCallback(props, 'onArticleDomainNavigate')
  const handleEmbedDisclose = usePropsCallback(props, 'onArticleEmbedDisclose')
  // onArticleSeen visibility observer
  const blockRef = React.useRef()
  use100PctVisibleViewportObserver({
    elementRef: blockRef,
    unobserveOnVisible: true,
    onVisible: usePropsCallback(props, 'onArticleSeen')
  })
  // Focus observer
  const [ is90PctVisible, is50PctVisible, is10PctVisible ] = useFocusObserver({
    focusRef: blockRef,
    onFocus: onArticleElementFocus
  })
  // Render
  return (
    tag('article', {
      ref: blockRef,
      className: classNames(
        props.className,
        bem.block({
          subtype: subtypeKey,
          focused: is50PctVisible
        })
      ),
      dataset: {
        urlId: article['url_id']
      },
      style: { height }
    },
      tag('div', {
        className: bem.element('inner')
      },
        tag(SeriesFeedArticleImage, {
          article,
          onContentNavigate: handleContentNavigate,
          onEmbedDisclose: handleEmbedDisclose
        }),
        tag('div', {
          className: bem.element('details')
        },
          tag('a', {
            className: bem.element('domain'),
            href: articleDomainSearchHref(article),
            onClick: handleDomainNavigate
          },
            article.hostname
          ),
          tag(SeriesFeedArticleContent, {
            article,
            isLineClampDisabled: !is10PctVisible,
            onContentNavigate: handleContentNavigate
          }),
          tag(SeriesFeedArticleCallToAction, {
            article,
            onContentNavigate: handleContentNavigate
          }),
          tag(SeriesFeedArticleRecReason, {
            recReason
          })
        ),
        tag(SeriesFeedArticleActions, {
          ...props,
          anchorId,
          className: bem.element('actions', {
            hidden: !is90PctVisible
          })
        })
      )
    )
  )
}

function SeriesFeedArticleImage({
  article,
  onContentNavigate,
  onEmbedDisclose
}) {
  const { embedUrl, image, md5image, url } = article
  const imageSources = React.useMemo(
    () => [ image, md5image ],
    [ image, md5image ]
  )
  return (
    tag('div', {
      className: bem.element('embed-container')
    },
      tag(LazyImage, {
        className: bem.element('image'),
        href: article.url,
        rel: 'external nofollow noopener',
        target: '_blank',
        sources: imageSources,
        onClick: onContentNavigate
      }),
      embedUrl && (
        tag(EmbeddedPlayer, {
          className: bem.element('embed-player'),
          src: embedUrl,
          fallbackUrl: url,
          onDisclose: onEmbedDisclose
        })
      )
    )
  )
}

function SeriesFeedArticleContent({
  article,
  isLineClampDisabled,
  onContentNavigate
}) {
  const titleRef = React.useRef()
  const titleStyle = useLineClampFitStyle({
    disabled: isLineClampDisabled,
    domRef: titleRef
  })
  const descriptionRef = React.useRef()
  const descriptionStyle = useLineClampFitStyle({
    disabled: !article.description || isLineClampDisabled,
    domRef: descriptionRef
  })
  return (
    tag('a', {
      className: bem.element('content'),
      href: article.url,
      rel: 'external nofollow noopener',
      target: '_blank',
      onClick: onContentNavigate
    },
      tag('p', {
        ref: titleRef,
        className: bem.element('title'),
        style: titleStyle
      },
        article.title
      ),
      !!article.description && (
        tag('p', {
          ref: descriptionRef,
          className: bem.element('description'),
          style: descriptionStyle
        },
          article.description
        )
      )
    )
  )
}

function SeriesFeedArticleRecReason({
  recReason
}) {
  const firstRecReasonItem = _.get(recReason, [ 'items', 0 ])
  if (firstRecReasonItem && firstRecReasonItem.type === modelTypes.USER) {
    return (
      tag(CapsuleButton, {
        className: bem.element('rec-reason'),
        href: userProfileHref(firstRecReasonItem)
      },
        tag(UserAvatar, {
          className: bem.element('rec-reason-avatar'),
          badge: false,
          user: firstRecReasonItem
        }),
        tag('label', {
          className: bem.element('rec-reason-label')
        },
          `@${firstRecReasonItem.username} saved this`
        )
      )
    )
  }
  return null
}

function SeriesFeedArticleCallToAction({
  article,
  onContentNavigate
}) {
  let text = 'Read more'
  if (article.subtype === articleSubtypes.VIDEO) {
    text = `Watch on ${article.hostname}`
  } else if (article.subtype === articleSubtypes.AUDIO) {
    text = `Listen on ${article.hostname}`
  }
  return (
    tag('a', {
      'aria-hidden': true,
      className: bem.element('call-to-action'),
      href: article.url,
      rel: 'external nofollow noopener',
      target: '_blank',
      onClick: onContentNavigate
    },
      text
    )
  )
}

/**
 * React hook to track whether the element is currently scrolled within the viewport
 * @param {Object} options
 * @param {React.RefObject<Element>} options.focusRef
 * @param {(element: Element) => void} options.onFocus
 * @returns {boolean}
 */
function useFocusObserver({ focusRef, onFocus }) {
  const [ is90PctVisible ] = use90PctVisibleViewportObserver({
    elementRef: focusRef,
    unobserveOnVisible: false
  })
  const [ is50PctVisible ] = use50PctVisibleViewportObserver({
    elementRef: focusRef,
    unobserveOnVisible: false
  })
  const [ is10PctVisible ] = use10PctVisibleViewportObserver({
    elementRef: focusRef,
    unobserveOnVisible: false
  })
  React.useEffect(
    () => {
      if (is90PctVisible) {
        onFocus(focusRef.current)
      }
    },
    [ is90PctVisible, focusRef, onFocus ]
  )
  return [ is90PctVisible, is50PctVisible, is10PctVisible ]
}

module.exports = SeriesFeedArticle
